import type { SupportedLanguages } from '../i18n';

export interface HotelConfig {
  // ID de l'hôtel dans Airtable
  hotel_id: string;

  meta: {
    title: string;
    description: string;
    keywords: string[];
    author: string;
    url: string;
    images: {
      default: {
        url: string;
        width: number;
        height: number;
        alt: string;
      };
      twitter?: {
        url: string;
        width: number;
        height: number;
        alt: string;
      };
    };
  };

  // Configuration de l'hôtel
  hotel: {
    name: string;
    fulladress: string;
    telephone: string;
    url?: string;
    booking?: string;
    defaultLanguage: SupportedLanguages;
    timezone: string;
    latitude: number;
    longitude: number;
  };

  pwa: {
    name: string;
    shortName: string;
    themeColor: string;
    backgroundColor: string;
    display: 'standalone' | 'minimal-ui' | 'fullscreen' | 'browser';
    orientation: 'portrait' | 'landscape' | 'any';
    icons: {
      small: string;
      large: string;
    };
  };

  theme: {
    colors: {
      major: string;
      dark: string;
      medium: string;
      light: string;
    };
    navigation: {
      text: string;
      icon: string;
      background: string;
      count: {
        background: string;
        text: string;
      };
    };
    borderRadius: string;
    font: {
      family: string;
      url?: string;
    };
    categoryColors: string[];
  };

  i18n: {
    defaultLanguage: SupportedLanguages;
    baseUrl: string;
    languages: SupportedLanguages[];
    tables: {
      translations: string;
      categoriesAndTypes: string;
      sections: string;
    };
  };

  analytics: {
    id: string;
    enabled: boolean;
    debug: boolean;
    customEvents: {
      pageViews: boolean;
      navigation: boolean;
      filters: boolean;
      detailCards: boolean;
      placeCards: boolean;
      actions: boolean;
      translations: boolean;
    };
  };

  serviceWorker: {
    enabled: boolean;
    caching: {
      staticAssets: {
        maxAge: number;
        maxItems: number;
      };
      api: {
        maxAge: number;
        maxItems: number;
      };
      images: {
        maxAge: number;
        maxItems: number;
      };
    };
  };

  viewport: {
    width: string;
    initialScale: number;
    maximumScale: number;
    userScalable: boolean;
    viewportFit: 'auto' | 'contain' | 'cover';
  };

  rgpd: {
    tables: {
      config: string;
      privacyPolicy: string;
    };
  };

  maps: {
    id: string;
    limitWalk: number;
  };
}

// Détecter l'environnement de développement
const isDev = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === undefined;

export const hotelConfig: HotelConfig = {
  // ID de l'hôtel R de Paris
  hotel_id: 'recUjysusCAXj3mET',

  meta: {
    title: 'Hôtel R de Paris - Votre concierge personnel',
    description: 'Votre concierge personnel pour tout simplifier ! Restaurants, spectacles et services utiles à deux pas de votre hôtel. Découvrez et réservez en quelques clics.',
    keywords: ['hôtel paris', 'concierge', 'restaurant paris', 'spectacle paris', 'service hôtelier'],
    author: 'Hôtel R de Paris',
    url: 'https://hotelrdeparis.conciergify.com',
    images: {
      default: {
        url: 'https://hotelrdeparis.conciergify.com/images/social/og-image.jpg',
        width: 1200,
        height: 630,
        alt: 'Hôtel R de Paris - Vue de la façade'
      },
      twitter: {
        url: 'https://hotelrdeparis.conciergify.com/images/social/twitter-card.jpg',
        width: 1200,
        height: 600,
        alt: 'Hôtel R de Paris - Vue de la façade'
      }
    }
  },

  hotel: {
    name: 'Hôtel R de Paris',
    fulladress: '41 Rue de Clichy, 75009 Paris',
    telephone: '+33 1 40 82 36 20',
    url: 'https://www.hotelrdeparis.com',
    booking: 'https://www.hotelrdeparis.com/booking',
    defaultLanguage: 'fr',
    timezone: 'Europe/Paris',
    latitude: 48.8793,
    longitude: 2.3289
  },

  pwa: {
    name: 'Hôtel R de Paris',
    shortName: 'Hôtel R',
    themeColor: '#414143',
    backgroundColor: '#414143',
    display: 'standalone',
    orientation: 'portrait',
    icons: {
      small: '/icon/icon_192.png',
      large: '/icon/icon_512.png'
    }
  },

  theme: {
    colors: {
      major: '#6B7280',
      dark: '#1F2937',
      medium: '#4B5563',
      light: '#F3F4F6'
    },
    navigation: {
      text: '#1F2937',
      icon: '#6B7280',
      background: 'rgba(255, 255, 255, 0.75)',
      count: {
        background: '#6B7280',
        text: '#FFFFFF'
      }
    },
    borderRadius: '0.5rem',
    font: {
      family: 'system-ui, -apple-system, sans-serif'
    },
    categoryColors: [
      '#6CB0C0', '#D4A6A6', '#8EA864', '#C06C6C', '#A78FC1',
      '#986C6C', '#6C6C6C', '#C28800', '#6C9E84', '#B0C06C',
      '#A8A8A8', '#A68EC0', '#C08E6C', '#AFA663', '#64A890'
    ]
  },

  i18n: {
    defaultLanguage: 'fr',
    baseUrl: 'https://hotelrdeparis.conciergify.com',
    languages: ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru'],
    tables: {
      translations: 'tblBG2QlG1BS4rtpz',
      categoriesAndTypes: 'tblr92Xn34PHlpySF',
      sections: 'tbl8Frrc4kAlmnvX7'
    }
  },

  analytics: {
    id: 'G-K7VGXEPVS3',
    enabled: true,
    debug: isDev,
    customEvents: {
      pageViews: true,
      navigation: true,
      filters: true,
      detailCards: true,
      placeCards: true,
      actions: true,
      translations: true
    }
  },

  serviceWorker: {
    enabled: true,
    caching: {
      staticAssets: {
        maxAge: 7 * 24 * 60 * 60 * 1000, // 7 jours
        maxItems: 100
      },
      api: {
        maxAge: 5 * 60 * 1000, // 5 minutes
        maxItems: 50
      },
      images: {
        maxAge: 30 * 24 * 60 * 60 * 1000, // 30 jours
        maxItems: 200
      }
    }
  },

  viewport: {
    width: 'device-width',
    initialScale: 1.0,
    maximumScale: 1.0,
    userScalable: false,
    viewportFit: 'cover'
  },

  rgpd: {
    tables: {
      config: 'tbl0p0KMnhoCYnazB',
      privacyPolicy: 'tblBeAqJdlGLUAnGn'
    }
  },

  maps: {
    id: '1617805941c4af2d',
    limitWalk: 30
  }
};